import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CookieItem, CookieGroup, ProviderGroup, DescriptionGroup } from '../interfaces/cookie-policy.interface';

@Injectable({
  providedIn: 'root'
})
export class CookiePolicyService {
  private readonly OSANO_DISCLOSURE_URL = 'https://disclosure.api.osano.com/customer/Azyw8nRjECr8jJxp/config/31aa2448-9c45-42dd-9e27-7a1f934c79ec';

  private readonly groupDescriptions: Record<string, string> = {
    'ESSENTIAL': 'These items are required to enable basic website functionality.',
    'MARKETING': 'These items are used to deliver advertising that is more relevant to you and your interests. They may also be used to limit the number of times you see an advertisement and measure the effectiveness of advertising campaigns. Advertising networks usually place them with the website operator\'s permission.',
    'ANALYTICS': 'These items help the website operator understand how its website performs, how visitors interact with the site, and whether there may be technical issues. This storage type usually doesn\'t collect information that identifies a visitor.',
    'PERSONALISATION': 'These items allow the website to remember choices you make (such as your user name, language, or the region you are in) and provide enhanced, more personal features. For example, a website may provide you with local weather reports or traffic news by storing data about your current location.'
  };

  constructor(private http: HttpClient) { }

  getCookieDisclosure(): Observable<CookieGroup[]> {
    return this.http.get<CookieItem[]>(this.OSANO_DISCLOSURE_URL).pipe(
      map(cookies => this.groupCookiesByClassification(cookies)),
      catchError(error => {
        console.error('Error fetching cookie disclosure:', error);
        return of([]);
      })
    );
  }

  private groupCookiesByClassification(cookies: CookieItem[]): CookieGroup[] {
    const grouped = new Map<string, CookieItem[]>();
    // Group cookies by classification
    cookies.forEach(cookie => {
      const classification = cookie.classification || 'ESSENTIAL';
      if (!grouped.has(classification)) {
        grouped.set(classification, []);
      }
      grouped.get(classification)!.push(cookie);
    });

    // Sort classifications in desired order
    const order = ['ESSENTIAL', 'MARKETING', 'ANALYTICS', 'PERSONALISATION'];
    const sortedGroups: CookieGroup[] = [];

    order.forEach(classification => {
      const classificationCookies = grouped.get(classification) || [];
      sortedGroups.push({
        title: this.formatClassificationTitle(classification),
        classification: classification,
        description: this.groupDescriptions[classification] || '',
        providerGroups: this.groupByProvider(classificationCookies),
        isExpanded: false
      });
    });

    // Add any remaining classifications not in the order list
    grouped.forEach((cookieList, classification) => {
      if (!order.includes(classification)) {
        sortedGroups.push({
          title: this.formatClassificationTitle(classification),
          classification: classification,
          description: this.groupDescriptions[classification] || '',
          providerGroups: this.groupByProvider(cookieList),
          isExpanded: false
        });
      }
    });

    return sortedGroups;
  }

  private groupByProvider(cookies: CookieItem[]): ProviderGroup[] {
    const providerMap = new Map<string, CookieItem[]>();
    cookies.forEach(cookie => {
      const provider = cookie.provider || cookie.name || 'Unknown';
      if (!providerMap.has(provider)) {
        providerMap.set(provider, []);
      }
      providerMap.get(provider)!.push(cookie);
    });

    return Array.from(providerMap.entries()).map(([provider, cookies]) => ({
      provider,
      type: cookies[0]?.type || '',
      descriptionGroups: this.groupByDescription(cookies)
    }));
  }

  private groupByDescription(cookies: CookieItem[]): DescriptionGroup[] {
    const descriptionMap = new Map<string, CookieItem[]>();
    cookies.forEach(cookie => {
      const description = cookie.purpose || '-';
      if (!descriptionMap.has(description)) {
        descriptionMap.set(description, []);
      }
      descriptionMap.get(description)!.push(cookie);
    });

    return Array.from(descriptionMap.entries()).map(([description, cookies]) => ({
      description,
      cookies
    }));
  }

  private formatClassificationTitle(classification: string): string {
    return classification.charAt(0) + classification.slice(1).toLowerCase() + ' cookies';
  }
}
